/**
 * (#32) Reglas del cronómetro y de la salida de la pantalla de entrenamiento.
 *
 * Viven acá y no dentro de `useTrainingPlanDetails` porque son decisiones puras
 * —qué tiempo se registra, si el reloj corre, a dónde vuelve el usuario— y ese
 * hook tiene 900 líneas de estado y efectos donde no se pueden probar.
 */

export interface ITimerState {
  /** Momento (epoch ms) desde el que se cuenta, o null si la sesión no arrancó. */
  sessionStartTime: number | null;
  /** Pausa manual del alumno, la del botón de play/pause. */
  isTimerPaused: boolean;
  /** True mientras está abierto alguno de los diálogos de finalización. */
  isFinishing: boolean;
}

/**
 * El cronómetro corre solo si la sesión arrancó y nadie la frenó.
 *
 * `isFinishing` es la parte nueva: al tocar "Finalizar" el reloj queda quieto,
 * así el tiempo que se guarda es el del entrenamiento y no incluye lo que el
 * alumno tardó en completar el feedback.
 */
export function shouldTimerRun({ sessionStartTime, isTimerPaused, isFinishing }: ITimerState): boolean {
  return Boolean(sessionStartTime) && !isTimerPaused && !isFinishing;
}

/**
 * Tiempo que se registra en la sesión.
 *
 * Se compara contra `null` a propósito: con `frozen || running`, un
 * entrenamiento congelado en 0 segundos devolvería el cronómetro en curso y se
 * saltearía la validación que avisa que la sesión no tiene tiempo.
 */
export function resolveSessionTime(frozenSeconds: number | null, runningSeconds: number): number {
  return frozenSeconds !== null ? frozenSeconds : runningSeconds;
}

/**
 * Nuevo `sessionStartTime` para reanudar sin que el cronómetro salte.
 *
 * Al retomar hay que correr el arranque hacia atrás tantos segundos como ya
 * llevaba: si se reanuda a las 10:00 con 5 minutos corridos, la sesión "arrancó"
 * a las 09:55.
 */
export function resumeStartTime(now: number, elapsedSeconds: number): number {
  return now - elapsedSeconds * 1000;
}

/** A dónde vuelve quien sale del entrenamiento sin finalizarlo. */
export function buildExitRoute(userType: string | undefined, clientId: number | null): string {
  if (userType === 'coach') {
    return clientId ? `/client-dashboard/${clientId}` : '/coach';
  }
  return '/student';
}
